import React, { useState, useRef, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import DotsBackground from "../components/DotsBackground.tsx";
import { ArrowsRightLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { MdContentCopy } from "react-icons/md";
import { IoCheckmark } from "react-icons/io5";
import LeftSidebar from "../components/LeftSidebar.tsx";
import RightSidebar from "../components/RightSidebar.tsx";
import BottomNav from "../components/BottomNav.tsx";
import TopProfileBar from "../components/TopProfileBar.tsx";
import { getUserData } from "../utils/userStorage.ts";
import { batakToLatin, latinToBatak } from "../utils/batakTransliteration.js";
import { Instagram, Linkedin } from "lucide-react";

const TerjemahanPage = () => {
  const [currentSection, setCurrentSection] = useState("Terjemahan");
  const [inputLang, setInputLang] = useState<"latin" | "batak">("latin");
  const [outputLang, setOutputLang] = useState<"batak" | "latin">("batak");
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const data = getUserData();
    return data?.meta?.darkMode ?? false;
  });

  // Responsive breakpoints
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1023 });
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLandscape = useMediaQuery({ orientation: "landscape" });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "galaxy_aksara_user_data") {
        const data = getUserData();
        setDarkMode(data?.meta?.darkMode ?? false);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Refs for input tabs
  const inputLatinRef = useRef<HTMLButtonElement>(null);
  const inputBatakRef = useRef<HTMLButtonElement>(null);
  const [inputUnderline, setInputUnderline] = useState({ left: 0, width: 0 });

  // Refs for output tabs
  const outputBatakRef = useRef<HTMLButtonElement>(null);
  const outputLatinRef = useRef<HTMLButtonElement>(null);
  const [outputUnderline, setOutputUnderline] = useState({ left: 0, width: 0 });

  // Add ref for the contentEditable div
  const inputContentRef = useRef<HTMLDivElement>(null);

  // Update input underline position
  useEffect(() => {
    const activeRef = inputLang === "latin" ? inputLatinRef : inputBatakRef;
    if (activeRef.current) {
      setInputUnderline({
        left: activeRef.current.offsetLeft,
        width: activeRef.current.offsetWidth,
      });
    }
  }, [inputLang]);

  // Update output underline position
  useEffect(() => {
    const activeRef = outputLang === "batak" ? outputBatakRef : outputLatinRef;
    if (activeRef.current) {
      setOutputUnderline({
        left: activeRef.current.offsetLeft,
        width: activeRef.current.offsetWidth,
      });
    }
  }, [outputLang]);

  // Automatically set outputLang when inputLang changes
  React.useEffect(() => {
    setOutputLang(inputLang === "latin" ? "batak" : "latin");
  }, [inputLang]);

  // Automatically translate when inputText or language changes
  React.useEffect(() => {
    if (inputLang === "latin" && outputLang === "batak") {
      setOutputText(latinToBatak(inputText));
    } else if (inputLang === "batak" && outputLang === "latin") {
      setOutputText(batakToLatin(inputText));
    } else {
      setOutputText(inputText);
    }
  }, [inputText, inputLang, outputLang]);

  // Switch languages
  const handleSwitch = () => {
    const newInputLang = outputLang;
    const newInputText = outputText;

    setInputLang(newInputLang);
    setInputText(newInputText);

    // Update the contentEditable div content
    if (inputContentRef.current) {
      inputContentRef.current.textContent = newInputText;
    }
  };

  const handleInputLangChange = (lang: "latin" | "batak") => {
    const newOutputLang = lang === "latin" ? "batak" : "latin";

    // If changing to opposite language and there's output text, swap the content
    if (outputText && outputLang !== newOutputLang) {
      setInputText(outputText);
      setInputLang(lang);
      setOutputLang(newOutputLang);

      // Update the contentEditable div with swapped content
      if (inputContentRef.current) {
        inputContentRef.current.textContent = outputText;
      }
    } else {
      // Normal language change - clear input
      setInputLang(lang);
      setOutputLang(newOutputLang);
      setInputText("");

      // Clear the contentEditable div
      if (inputContentRef.current) {
        inputContentRef.current.textContent = "";
      }
    }
  };

  const handleOutputLangChange = (lang: "latin" | "batak") => {
    const newInputLang = lang === "latin" ? "batak" : "latin";

    // If changing to opposite language and there's input text, swap the content
    if (inputText && inputLang !== newInputLang) {
      setOutputLang(lang);
      setInputLang(newInputLang);
      setInputText(inputText); // Keep current input text which will be translated to new output

      // Update the contentEditable div - keep current content
      if (inputContentRef.current) {
        inputContentRef.current.textContent = inputText;
      }
    } else {
      // Normal language change - clear input
      setOutputLang(lang);
      setInputLang(newInputLang);
      setInputText("");

      // Clear the contentEditable div
      if (inputContentRef.current) {
        inputContentRef.current.textContent = "";
      }
    }
  };

  // Update contentEditable when inputText changes programmatically
  useEffect(() => {
    if (
      inputContentRef.current &&
      inputContentRef.current.textContent !== inputText
    ) {
      inputContentRef.current.textContent = inputText;
    }
  }, [inputText]);

  return (
    <div
      className={`min-h-screen relative flex ${
        darkMode ? "bg-gray-900" : "bg-gray-100"
      }`}
    >
      <DotsBackground darkMode={darkMode} />
      {/* Copy success indicator - always visible on all modes */}
      {copySuccess && (
        <div
          className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50"
          style={{ fontFamily: "DIN Next Rounded" }}
        >
          <div
            className={`px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in-up ${
              darkMode
                ? "bg-gray-700 text-gray-100 border-2 border-gray-600"
                : "bg-gray-200 text-gray-800 border-2 border-gray-300"
            }`}
          >
            <IoCheckmark className="w-4 h-4 text-green-400" />
            <span className="text-sm">Teks disalin</span>
          </div>
        </div>
      )}
      {isDesktop && (
        <LeftSidebar
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          darkMode={darkMode}
        />
      )}
      {isTablet && !isMobile && (
        <LeftSidebar
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          darkMode={darkMode}
          compact
        />
      )}
      {/* Main Content Layout */}
      {isMobile || isTablet ? (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              zIndex: 30,
            }}
          >
            {isMobile && (
              <TopProfileBar darkMode={darkMode}>
                {/* Only show profile section from RightSidebar */}
                <RightSidebar darkMode={darkMode} profileOnly />
              </TopProfileBar>
            )}
          </div>
          <main
            className="flex-1 relative z-10 bg-transparent"
            style={{
              paddingTop: 64,
              paddingBottom: 64,
              paddingLeft: 12,
              paddingRight: 12,
            }}
          >
            <div
              className={`max-w-4xl mx-auto ${
                isMobile ? "py-10" : "py-0"
              } px-2`}
            >
              <h1
                className={`text-3xl font-bold mb-6 text-center ${
                  darkMode ? "text-gray-100" : "text-gray-800"
                }`}
                style={{ fontFamily: "Feather Bold" }}
              >
                Terjemahan
              </h1>
              <div
                className={`rounded-xl shadow p-4 flex flex-col gap-4 ${
                  darkMode
                    ? "bg-gray-800 border border-gray-700"
                    : "bg-white border border-gray-200"
                }`}
              >
                {/* Language indicator and switch */}
                <div className="flex items-center justify-center gap-2">
                  {/* Input Language Tabs */}
                  <div className="relative flex">
                    <button
                      ref={inputLatinRef}
                      className={`px-3 py-2 font-semibold transition-colors duration-200 rounded-md focus:outline-none ${
                        inputLang === "latin"
                          ? darkMode
                            ? "text-blue-400 bg-gray-800 cursor-default"
                            : "text-blue-600 bg-white cursor-default"
                          : darkMode
                          ? "text-gray-400 hover:bg-gray-700 cursor-pointer"
                          : "text-gray-500 hover:bg-gray-100 cursor-pointer"
                      }`}
                      onClick={() =>
                        inputLang !== "latin" && handleInputLangChange("latin")
                      }
                      disabled={inputLang === "latin"}
                    >
                      Latin
                    </button>
                    <button
                      ref={inputBatakRef}
                      className={`px-3 py-2 font-semibold transition-colors duration-200 rounded-md focus:outline-none ${
                        inputLang === "batak"
                          ? darkMode
                            ? "text-blue-400 bg-gray-800 cursor-default"
                            : "text-blue-600 bg-white cursor-default"
                          : darkMode
                          ? "text-gray-400 hover:bg-gray-700 cursor-pointer"
                          : "text-gray-500 hover:bg-gray-100 cursor-pointer"
                      }`}
                      onClick={() =>
                        inputLang !== "batak" && handleInputLangChange("batak")
                      }
                      disabled={inputLang === "batak"}
                    >
                      Batak Toba
                    </button>
                    {/* Animated underline */}
                    <div
                      className={`absolute bottom-0 h-0.5 transition-all duration-300 ease-out ${
                        darkMode ? "bg-blue-400" : "bg-blue-600"
                      }`}
                      style={{
                        left: inputUnderline.left,
                        width: inputUnderline.width,
                      }}
                    />
                  </div>
                  {/* Switch Button */}
                  <button
                    className={`mx-1 rounded-full p-2 duration-200 transform hover:-translate-y-0.5 transition-all ${
                      darkMode ? "hover:bg-gray-600" : "hover:bg-gray-200"
                    }`}
                    title="Tukar bahasa"
                    onClick={handleSwitch}
                  >
                    <ArrowsRightLeftIcon
                      className={`w-5 h-5 ${
                        darkMode ? "text-gray-300" : "text-gray-500"
                      }`}
                    />
                  </button>
                  {/* Output Language Tabs */}
                  <div className="relative flex">
                    <button
                      ref={outputBatakRef}
                      className={`px-3 py-2 font-semibold transition-colors duration-200 rounded-md focus:outline-none ${
                        outputLang === "batak"
                          ? darkMode
                            ? "text-blue-400 bg-gray-800 cursor-default"
                            : "text-blue-600 bg-white cursor-default"
                          : darkMode
                          ? "text-gray-400 hover:bg-gray-700 cursor-pointer"
                          : "text-gray-500 hover:bg-gray-100 cursor-pointer"
                      }`}
                      onClick={() =>
                        outputLang !== "batak" &&
                        handleOutputLangChange("batak")
                      }
                      disabled={outputLang === "batak"}
                    >
                      Batak Toba
                    </button>
                    <button
                      ref={outputLatinRef}
                      className={`px-3 py-2 font-semibold transition-colors duration-200 rounded-md focus:outline-none ${
                        outputLang === "latin"
                          ? darkMode
                            ? "text-blue-400 bg-gray-800 cursor-default"
                            : "text-blue-600 bg-white cursor-default"
                          : darkMode
                          ? "text-gray-400 hover:bg-gray-700 cursor-pointer"
                          : "text-gray-500 hover:bg-gray-100 cursor-pointer"
                      }`}
                      onClick={() =>
                        outputLang !== "latin" &&
                        handleOutputLangChange("latin")
                      }
                      disabled={outputLang === "latin"}
                    >
                      Latin
                    </button>
                    {/* Animated underline */}
                    <div
                      className={`absolute bottom-0 h-0.5 transition-all duration-300 ease-out ${
                        darkMode ? "bg-blue-400" : "bg-blue-600"
                      }`}
                      style={{
                        left: outputUnderline.left,
                        width: outputUnderline.width,
                      }}
                    />
                  </div>
                </div>
                {/* Input Card */}
                <div
                  className={`w-full rounded-lg p-3 flex flex-col justify-between min-h-[140px] relative ${
                    darkMode
                      ? "bg-gray-800 border border-gray-700 text-gray-100"
                      : "bg-white border border-gray-200 text-gray-800"
                  }`}
                >
                  {inputText && (
                    <button
                      className={
                        "absolute top-2 right-2 p-1 rounded-full transition-colors duration-200 z-10"
                      }
                      onClick={() => {
                        setInputText("");
                        if (inputContentRef.current) {
                          inputContentRef.current.textContent = "";
                          inputContentRef.current.focus();
                        }
                      }}
                      title="Hapus"
                    >
                      <XMarkIcon
                        className={`w-4 h-4 ${
                          darkMode ? "text-gray-200" : "text-gray-600"
                        }`}
                      />
                    </button>
                  )}
                  <div className="flex-1 overflow-hidden">
                    <div
                      ref={inputContentRef}
                      className={`w-full text-lg whitespace-pre-wrap break-words min-h-[80px] ${
                        darkMode ? "text-gray-100" : "text-gray-800"
                      }`}
                      contentEditable
                      suppressContentEditableWarning={true}
                      onInput={(e) =>
                        setInputText(e.currentTarget.textContent || "")
                      }
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedText =
                          e.clipboardData.getData("text/plain");
                        const selection = window.getSelection();
                        if (selection && selection.rangeCount > 0) {
                          const range = selection.getRangeAt(0);
                          range.deleteContents();
                          const textNode = document.createTextNode(pastedText);
                          range.insertNode(textNode);
                          range.setStartAfter(textNode);
                          range.setEndAfter(textNode);
                          selection.removeAllRanges();
                          selection.addRange(range);
                        }
                        setInputText(e.currentTarget.textContent || "");
                      }}
                      style={{
                        fontFamily: "Noto Sans Batak, DIN Next Rounded",
                        outline: "none",
                        border: "none",
                        background: "transparent",
                        overflow: "hidden",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        paddingRight: inputText ? "24px" : "0",
                      }}
                      data-placeholder={
                        inputLang === "latin"
                          ? "Masukkan teks Latin di sini..."
                          : "Masukkan aksara Batak di sini..."
                      }
                    >
                      {/* Remove {inputText} from here - it causes cursor positioning issues */}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span
                      className={`text-xs ${
                        darkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      {inputText.length} / 5.000
                    </span>
                  </div>
                </div>
                {/* Output Card */}
                <div
                  className={`w-full rounded-lg p-3 min-h-[140px] flex flex-col justify-start relative ${
                    darkMode
                      ? "bg-gray-700 border border-gray-700"
                      : "bg-gray-100 border border-gray-200"
                  }`}
                >
                  {outputText && (
                    <button
                      className={
                        "absolute bottom-2 right-2 p-2 rounded-full z-10 duration-200 transform hover:-translate-y-0.5 transition-all"
                      }
                      onClick={async () => {
                        let success = false;
                        // Try Clipboard API first
                        if (navigator.clipboard && window.isSecureContext) {
                          try {
                            await navigator.clipboard.writeText(outputText);
                            success = true;
                          } catch (err) {
                            success = false;
                          }
                        }
                        // Fallback for mobile browsers
                        if (!success) {
                          try {
                            const textarea = document.createElement("textarea");
                            textarea.value = outputText;
                            textarea.style.position = "fixed";
                            textarea.style.top = "-1000px";
                            textarea.style.left = "-1000px";
                            document.body.appendChild(textarea);
                            textarea.focus();
                            textarea.select();
                            const result = document.execCommand("copy");
                            document.body.removeChild(textarea);
                            success = result;
                          } catch (err) {
                            success = false;
                          }
                        }
                        setCopySuccess(true);
                        setTimeout(() => setCopySuccess(false), 2000);
                      }}
                      title="Salin"
                    >
                      <MdContentCopy
                        className={`w-4 h-4 ${
                          darkMode ? "text-white" : "text-gray-700"
                        }`}
                      />
                    </button>
                  )}
                  <div
                    className={`text-lg break-words ${
                      darkMode ? "text-gray-100" : "text-gray-800"
                    }`}
                    style={{
                      fontFamily: "Noto Sans Batak, DIN Next Rounded",
                      minHeight: "60px",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      paddingBottom: outputText ? "36px" : "0",
                    }}
                  >
                    {outputText || (
                      <span
                        className={darkMode ? "text-gray-500" : "text-gray-400"}
                        style={{
                          fontFamily: "Noto Sans Batak, DIN Next Rounded",
                        }}
                      >
                        Hasil terjemahan akan muncul di sini...
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {(isMobile) && (
              <footer className="w-full pt-2 md:pt-2 pb-12 md:pb-10 bg-transparent flex flex-col items-center gap-2 z-10 relative">
                <div
                  className="text-gray-500 text-sm font-semibold"
                  style={{ fontFamily: "DIN Next Rounded" }}
                >
                  &copy; 2025 Galaxy Developer
                </div>
                <div className="flex gap-4 mt-1">
                  <a
                    href="https://www.instagram.com/galaxy.developer/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-gray-500 hover:text-pink-500 transition font-semibold text-sm"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/galaxydeveloper"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-gray-500 hover:text-blue-700 transition font-semibold text-sm"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                </div>
              </footer>
            )}
          </main>
        </>
      ) : (
        <div className="flex-1 flex flex-col">
          <main
            className="flex-1 relative z-10 bg-transparent"
            style={
              isTablet
                ? {
                    paddingTop: 32,
                    paddingBottom: 32,
                    paddingLeft: 32,
                    paddingRight: 32,
                  }
                : {
                    paddingTop: 0,
                    paddingBottom: 48,
                    paddingLeft: 0,
                    paddingRight: 0,
                  }
            }
          >
            <div className="max-w-4xl mx-auto py-10 px-4">
              <h1
                className={`text-4xl font-bold mb-8 text-center ${
                  darkMode ? "text-gray-100" : "text-gray-800"
                }`}
                style={{ fontFamily: "Feather Bold" }}
              >
                Terjemahan
              </h1>
              <div
                className={`rounded-xl shadow p-6 flex flex-col gap-6 ${
                  darkMode
                    ? "bg-gray-800 border border-gray-700"
                    : "bg-white border border-gray-200"
                }`}
              >
                {/* Language indicator and switch */}
                <div className="flex items-center justify-center gap-4">
                  {/* Input Language Tabs */}
                  <div className="relative flex">
                    <button
                      ref={inputLatinRef}
                      className={`px-4 py-2 font-semibold transition-colors duration-200 rounded-md focus:outline-none ${
                        inputLang === "latin"
                          ? darkMode
                            ? "text-blue-400 bg-gray-800 cursor-default"
                            : "text-blue-600 bg-white cursor-default"
                          : darkMode
                          ? "text-gray-400 hover:bg-gray-700 cursor-pointer"
                          : "text-gray-500 hover:bg-gray-100 cursor-pointer"
                      }`}
                      onClick={() =>
                        inputLang !== "latin" && handleInputLangChange("latin")
                      }
                      disabled={inputLang === "latin"}
                    >
                      Latin
                    </button>
                    <button
                      ref={inputBatakRef}
                      className={`px-4 py-2 font-semibold transition-colors duration-200 rounded-md focus:outline-none ${
                        inputLang === "batak"
                          ? darkMode
                            ? "text-blue-400 bg-gray-800 cursor-default"
                            : "text-blue-600 bg-white cursor-default"
                          : darkMode
                          ? "text-gray-400 hover:bg-gray-700 cursor-pointer"
                          : "text-gray-500 hover:bg-gray-100 cursor-pointer"
                      }`}
                      onClick={() =>
                        inputLang !== "batak" && handleInputLangChange("batak")
                      }
                      disabled={inputLang === "batak"}
                    >
                      Batak Toba
                    </button>
                    {/* Animated underline */}
                    <div
                      className={`absolute bottom-0 h-0.5 transition-all duration-300 ease-out ${
                        darkMode ? "bg-blue-400" : "bg-blue-600"
                      }`}
                      style={{
                        left: inputUnderline.left,
                        width: inputUnderline.width,
                      }}
                    />
                  </div>
                  {/* Switch Button */}
                  <button
                    className={`mx-2 rounded-full p-2 duration-200 transform hover:-translate-y-0.5 transition-all ${
                      darkMode ? "hover:bg-gray-600" : "hover:bg-gray-200"
                    }`}
                    title="Tukar bahasa"
                    onClick={handleSwitch}
                  >
                    <ArrowsRightLeftIcon
                      className={`w-5 h-5 ${
                        darkMode ? "text-gray-300" : "text-gray-500"
                      }`}
                    />
                  </button>
                  {/* Output Language Tabs */}
                  <div className="relative flex">
                    <button
                      ref={outputBatakRef}
                      className={`px-4 py-2 font-semibold transition-colors duration-200 rounded-md focus:outline-none ${
                        outputLang === "batak"
                          ? darkMode
                            ? "text-blue-400 bg-gray-800 cursor-default"
                            : "text-blue-600 bg-white cursor-default"
                          : darkMode
                          ? "text-gray-400 hover:bg-gray-700 cursor-pointer"
                          : "text-gray-500 hover:bg-gray-100 cursor-pointer"
                      }`}
                      onClick={() =>
                        outputLang !== "batak" &&
                        handleOutputLangChange("batak")
                      }
                      disabled={outputLang === "batak"}
                    >
                      Batak Toba
                    </button>
                    <button
                      ref={outputLatinRef}
                      className={`px-4 py-2 font-semibold transition-colors duration-200 rounded-md focus:outline-none ${
                        outputLang === "latin"
                          ? darkMode
                            ? "text-blue-400 bg-gray-800 cursor-default"
                            : "text-blue-600 bg-white cursor-default"
                          : darkMode
                          ? "text-gray-400 hover:bg-gray-700 cursor-pointer"
                          : "text-gray-500 hover:bg-gray-100 cursor-pointer"
                      }`}
                      onClick={() =>
                        outputLang !== "latin" &&
                        handleOutputLangChange("latin")
                      }
                      disabled={outputLang === "latin"}
                    >
                      Latin
                    </button>
                    {/* Animated underline */}
                    <div
                      className={`absolute bottom-0 h-0.5 transition-all duration-300 ease-out ${
                        darkMode ? "bg-blue-400" : "bg-blue-600"
                      }`}
                      style={{
                        left: outputUnderline.left,
                        width: outputUnderline.width,
                      }}
                    />
                  </div>
                </div>
                <div className="flex flex-col md:flex-row gap-4 items-stretch">
                  {/* Input Card */}
                  <div
                    className={`w-full md:w-1/2 md:max-w-[400px] md:min-w-[320px] rounded-lg p-4 flex flex-col justify-between min-h-[170px] relative ${
                      darkMode
                        ? "bg-gray-800 border border-gray-700 text-gray-100"
                        : "bg-white border border-gray-200 text-gray-800"
                    }`}
                  >
                    {inputText && (
                      <button
                        className={
                          "absolute top-2 right-2 p-1 rounded-full transition-colors duration-200 z-10"
                        }
                        onClick={() => {
                          setInputText("");
                          if (inputContentRef.current) {
                            inputContentRef.current.textContent = "";
                            inputContentRef.current.focus();
                          }
                        }}
                        title="Hapus"
                      >
                        <XMarkIcon
                          className={`w-4 h-4 ${
                            darkMode ? "text-gray-200" : "text-gray-600"
                          }`}
                        />
                      </button>
                    )}
                    <div className="flex-1 overflow-hidden">
                      <div
                        ref={inputContentRef}
                        className={`w-full text-xl whitespace-pre-wrap break-words min-h-[100px] ${
                          darkMode ? "text-gray-100" : "text-gray-800"
                        }`}
                        contentEditable
                        suppressContentEditableWarning={true}
                        onInput={(e) =>
                          setInputText(e.currentTarget.textContent || "")
                        }
                        onPaste={(e) => {
                          e.preventDefault();
                          const pastedText =
                            e.clipboardData.getData("text/plain");
                          const selection = window.getSelection();
                          if (selection && selection.rangeCount > 0) {
                            const range = selection.getRangeAt(0);
                            range.deleteContents();
                            const textNode =
                              document.createTextNode(pastedText);
                            range.insertNode(textNode);
                            range.setStartAfter(textNode);
                            range.setEndAfter(textNode);
                            selection.removeAllRanges();
                            selection.addRange(range);
                          }
                          setInputText(e.currentTarget.textContent || "");
                        }}
                        style={{
                          fontFamily: "Noto Sans Batak, DIN Next Rounded",
                          outline: "none",
                          border: "none",
                          background: "transparent",
                          overflow: "hidden",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          paddingRight: inputText ? "24px" : "0",
                        }}
                        data-placeholder={
                          inputLang === "latin"
                            ? "Masukkan teks Latin di sini..."
                            : "Masukkan aksara Batak di sini..."
                        }
                      >
                        {/* Remove {inputText} from here - it causes cursor positioning issues */}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span
                        className={`text-xs ${
                          darkMode ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        {inputText.length} / 5.000
                      </span>
                    </div>
                  </div>
                  {/* Output Card */}
                  <div
                    className={`w-full md:w-1/2 md:max-w-[400px] md:min-w-[320px] rounded-lg p-4 min-h-[170px] flex flex-col justify-start relative ${
                      darkMode
                        ? "bg-gray-700 border border-gray-700"
                        : "bg-gray-100 border border-gray-200"
                    }`}
                  >
                    {outputText && (
                      <button
                        className={
                          "absolute bottom-2 right-2 p-2 rounded-full z-10 duration-200 transform hover:-translate-y-0.5 transition-all"
                        }
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(outputText);
                            setCopySuccess(true);
                            setTimeout(() => setCopySuccess(false), 2000);
                          } catch (err) {
                            console.error("Failed to copy text: ", err);
                          }
                        }}
                        title="Salin"
                      >
                        <MdContentCopy
                          className={`w-4 h-4 ${
                            darkMode ? "text-white" : "text-gray-700"
                          }`}
                        />
                      </button>
                    )}
                    <div
                      className={`text-xl break-words ${
                        darkMode ? "text-gray-100" : "text-gray-800"
                      }`}
                      style={{
                        fontFamily: "Noto Sans Batak, DIN Next Rounded",
                        minHeight: "80px",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        paddingBottom: outputText ? "36px" : "0",
                      }}
                    >
                      {outputText || (
                        <span
                          className={
                            darkMode ? "text-gray-500" : "text-gray-400"
                          }
                          style={{
                            fontFamily: "Noto Sans Batak, DIN Next Rounded",
                          }}
                        >
                          Hasil terjemahan akan muncul di sini...
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      )}
      {(isDesktop || isTablet) && <RightSidebar darkMode={darkMode} />}
      {/* Bottom Nav (mobile only) */}
      {isMobile && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100vw",
            zIndex: 30,
            paddingLeft: 8,
            paddingRight: 8,
          }}
        >
          <BottomNav
            currentSection={currentSection}
            setCurrentSection={setCurrentSection}
            darkMode={darkMode}
          />
        </div>
      )}
      {/* Updated CSS with copy notification animation */}
      <style>{`
        [contentEditable][data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translate(-50%, 10px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TerjemahanPage;
